import {WritableDraft, produce} from "immer";

import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";

import * as dates from "@client/dates";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";

import * as forms from "@client/site/forms";
import {AppDispatch, GetState} from "@client/store";
import {server} from "@reactivated";

// Meal.hour is an extended timeline of the day: 0-23 are clock hours of the
// day's date, LATE_MEAL_HOUR and beyond are the next calendar morning that
// still belongs to this day (the late-night tail), up to MAX_MEAL_HOUR (noon).
export const LATE_MEAL_HOUR = 24;
export const MAX_MEAL_HOUR = 36;

export function hourToName(hour: number) {
    const clock = hour % 24;
    const hour12 = clock % 12 || 12;
    return `${hour12} ${clock < 12 ? "am" : "pm"}`;
}

// Journal display label. Only the first late slot is annotated: in a day
// sorted by hour, "12 am (late)" after "11 pm" makes the "1 am" that follows
// unambiguous.
export function hourToLabel(hour: number) {
    return hour === LATE_MEAL_HOUR ? `${hourToName(hour)} (late)` : hourToName(hour);
}

// The day is picked before logging, so the slot follows from the clock: after
// midnight but logging onto yesterday means the late tail of that day.
export function deriveMealHour(now: Date, dayKey: string) {
    const hour = now.getHours();
    const day = dates.parseISO(dayKey);

    // Meal plans key their days by pk, not date; they have no late tail.
    if (!dates.isValid(day)) {
        return hour;
    }

    const dayAfter = dates.addDays(day, 1);
    if (
        dates.format(now, "yyyy-MM-dd") === dates.format(dayAfter, "yyyy-MM-dd") &&
        hour <= MAX_MEAL_HOUR - LATE_MEAL_HOUR
    ) {
        return hour + LATE_MEAL_HOUR;
    }
    return hour;
}

export function autoStagedMeal(
    now: Date,
    dayKey: string,
): server.journal.nutrition.StagedMeal.output {
    const hour = deriveMealHour(now, dayKey);
    return {name: hourToName(hour), type: "AUTO_CREATED", hour};
}

// Auto meals share names across slots ("1 am" is both hour 1 and hour 25), so
// they match on hour; named meals match on name.
export function mealMatches(
    meal: {name: string; hour: number | null},
    staged: server.journal.nutrition.StagedMeal.output,
) {
    if (staged.hour != null) {
        return meal.hour === staged.hour;
    }
    return meal.hour == null && meal.name === staged.name;
}

function mealSortValue(meal: {hour: number | null}) {
    return meal.hour ?? -1;
}

export function addMealToDayDraft(
    dayDraft: WritableDraft<server.journal.nutrition.Day.output>,
    meal: WritableDraft<server.journal.nutrition.Meal.output>,
) {
    const mealHour = meal.hour;

    if (mealHour != null) {
        const insertIndex = dayDraft.meals.findIndex(
            (m) => m.hour != null && m.hour > mealHour,
        );

        if (insertIndex !== -1) {
            dayDraft.meals.splice(insertIndex, 0, meal);
            return;
        }
    }
    dayDraft.meals.push(meal);
}

export const sortMealsAndEntries =
    (day: server.journal.nutrition.Day.output, type: "meals" | "entries") => () => {
        void server.journal.day.update_day(
            {user_id: day.user_id, key: day.key},
            {...day, deleted_meal_uuids: []},
        );

        if (type === "meals") {
            void analytics.track("SORT_MEALS_SUCCESS");
        } else {
            void analytics.track("SORT_ENTRIES_SUCCESS");
        }
    };

export const swapMeal = (
    day: server.journal.nutrition.Day.output,
    sourceIndex: number,
    destinationIndex: number,
) => {
    return produce(day, (draftState) => {
        const source = draftState.meals[sourceIndex];

        draftState.meals.splice(sourceIndex, 1);
        draftState.meals.splice(destinationIndex, 0, source);
    });
};

export const swapEntry = (
    day: server.journal.nutrition.Day.output,
    sourceIndex: number,
    sourceMealId: UUID,
    destinationIndex: number,
    destinationMealId: UUID,
) => {
    return produce(day, (draftState) => {
        const sourceMeal = draftState.meals.find((m) => m.uuid === sourceMealId)!;
        const destinationMeal = draftState.meals.find(
            (m) => m.uuid == destinationMealId,
        )!;
        const source = sourceMeal.entries[sourceIndex];
        sourceMeal.entries.splice(sourceIndex, 1);
        destinationMeal.entries.splice(destinationIndex, 0, source);
    });
};

export const confirmAndDeleteEntry =
    (
        container: {key: string} | {recipeId: number},
        entry: server.journal.nutrition.Entry.output,
    ) =>
    async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body: "This will be deleted forever.",
            }),
        );

        if (confirmed) {
            await server.journal.nutrition.delete_entry({form: entry.uuid});
            dispatch(notify(`Your entry was deleted`));

            if ("recipeId" in container) {
                router.push("update_recipe.recipe", {recipeId: container.recipeId});
                void analytics.track("DELETE_RECIPE_ENTRY_SUCCESS");
            } else {
                router.push("journal.journal", {key: container.key});
                void analytics.track("DELETE_JOURNAL_ENTRY_SUCCESS");
            }
        }
    };

export const expandEntry =
    (
        day: server.journal.nutrition.Day.output,
        entry: server.journal.nutrition.Entry.output,
    ) =>
    async (dispatch: AppDispatch) => {
        const result = await server.journal.nutrition.expand_entry({
            entry_uuid: entry.uuid,
        });

        if (result.type === "success") {
            dispatch(notify(`Recipe expanded into its ingredients.`));
            void analytics.track("EXPAND_JOURNAL_ENTRY");
            router.push("journal.journal", {key: day.key});
        }
    };

export const pickDateAndCopyMeal =
    (source: server.journal.nutrition.Meal.output) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const profile = orm.getProfile(state)!;
        const pickedDate = await dispatch(dialogs.pickDate(source.key));

        if (pickedDate == null) {
            return;
        }

        const result = await server.journal.nutrition.copy_meal({
            user_id: profile.user.id,
            key: pickedDate,
            source_meal_uuid: source.uuid,
        });

        if (result.type === "success") {
            void analytics.track("COPY_MEAL_SUCCESS");
            dispatch(
                notify(
                    `Meal copied to ${pickedDate}. Switched to the day with the copied meal.`,
                ),
            );
            router.push("journal.journal", {key: pickedDate});
        }
    };

export const pickDateAndMoveMeal =
    (source: server.journal.nutrition.Meal.output) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const profile = orm.getProfile(state)!;
        const pickedDate = await dispatch(dialogs.pickDate(source.key));

        if (pickedDate == null) {
            return;
        }

        const result = await server.journal.nutrition.move_meal({
            user_id: profile.user.id,
            key: pickedDate,
            source_meal_uuid: source.uuid,
        });

        if (result.type === "success") {
            void analytics.track("MOVE_MEAL_SUCCESS");
            dispatch(
                notify(
                    `Meal moved to ${pickedDate}. Switched to the day with the moved meal.`,
                ),
            );
            router.push("journal.journal", {key: pickedDate});
        }
    };

export const confirmAndDeleteMeal =
    (meal: server.journal.nutrition.Meal.output) => async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body: "This will be deleted forever.",
            }),
        );

        if (confirmed) {
            const result = await server.journal.nutrition.delete_meal({
                meal_uuid: meal.uuid,
            });

            if (result.type === "success") {
                void analytics.track("DELETE_MEAL_SUCCESS");
                dispatch(notify(`Your meal was deleted`));
                router.push("journal.journal", {key: meal.key});
            }
        }
    };

export const updateMeasurements =
    (
        journal: server.journal.day.Journal.output,
        form: forms.PFormHandler<server.journal.day.MeasurementForm>,
    ) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const profile = orm.getProfile(state)!;

        const result = await server.journal.day.update_measurements({
            day: {
                key: journal.day.key,
                user_id: profile.user.id,
            },
            weight: form.values.weight ?? null,
            waist: form.values.waist ?? null,
            navel: form.values.navel ?? null,
            body_fat: form.values.body_fat ?? null,
            hips: form.values.hips ?? null,
            neck: form.values.neck ?? null,
            thigh: form.values.thigh ?? null,
            bicep: form.values.bicep ?? null,
            chest: form.values.chest ?? null,
        });

        if (result.type == "success") {
            dispatch(notify("Measurements updated"));
            void analytics.track("SAVE_MEASUREMENT_SUCCESS");
            router.push(
                "journal.journal",
                {key: journal.day.key},
                {journal: {...journal, day: result.data}},
            );
        }
    };

export const pickDateAndCopyDay =
    (source: server.journal.nutrition.Day.output) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const profile = orm.getProfile(state)!;
        const pickedDate = await dispatch(dialogs.pickDate(source.key));

        if (pickedDate == null) {
            return;
        }

        const result = await server.journal.day.copy_day({
            user_id: profile.user.id,
            key: pickedDate,
            source_user_id: source.user_id,
            source_day_key: source.key,
        });

        if (result.type === "success") {
            void analytics.track("COPY_DAY_SUCCESS");
            dispatch(
                notify(
                    `Day copied to ${pickedDate}. Switched to the day with the copied day.`,
                ),
            );
            router.push("journal.journal", {key: pickedDate});
        }
    };

export const pickDateAndMoveDay =
    (source: server.journal.nutrition.Day.output) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const profile = orm.getProfile(state)!;
        const pickedDate = await dispatch(dialogs.pickDate(source.key));

        if (pickedDate == null) {
            return;
        }

        const result = await server.journal.day.move_day({
            user_id: profile.user.id,
            key: pickedDate,
            source_user_id: source.user_id,
            source_day_key: source.key,
        });

        if (result.type === "success") {
            void analytics.track("MOVE_DAY_SUCCESS");
            dispatch(
                notify(
                    `Day moved to ${pickedDate}. Switched to the day with the moved day.`,
                ),
            );
            router.push("journal.journal", {key: pickedDate});
        }
    };

export const pickDateAndGoToDay =
    (
        focusOnDate: DateValue,
        currentTab: "journal" | "summary" | "workout" | "group" | "profile",
    ) =>
    async (dispatch: AppDispatch) => {
        const pickedDate = await dispatch(dialogs.pickDate(focusOnDate));

        if (pickedDate == null) {
            return;
        }

        router.push(`journal.${currentTab}`, {key: pickedDate});
    };

export const publishEntry =
    (entry: server.journal.nutrition.Entry.output) => async (dispatch: AppDispatch) => {
        const result = await server.journal.nutrition.publish_entry({
            entry_uuid: entry.uuid,
        });

        if (result.type === "success") {
            dispatch({
                type: "SHARE_SUCCESS",
                share: {type: "entry", share_uuid: result.data},
            });
        }
    };

export function deleteEntryInJournal({
    journal,
    entry,
    deleteMealIfEmpty,
}: {
    journal: server.journal.day.Journal.output;
    entry: server.journal.nutrition.ExpandedEntry.output;
    deleteMealIfEmpty?: true;
}) {
    const sourceMeal = journal.day.meals.find((meal) =>
        meal.entries.some((e) => e.uuid == entry.uuid),
    )!;

    const updatedJournal = produce(journal, (draft) => {
        const sourceMealDraft = draft.day.meals.find(
            (meal) => meal.uuid == sourceMeal.uuid,
        )!;
        const sourceIndex = sourceMealDraft.entries.findIndex(
            (e) => e.uuid == entry.uuid,
        );
        sourceMealDraft.entries.splice(sourceIndex, 1);

        if (deleteMealIfEmpty == true && sourceMealDraft.entries.length == 0) {
            draft.day.meals = draft.day.meals.filter(
                (m) => m.uuid != sourceMealDraft.uuid,
            );
        }
    });

    const deleteSourceMeal =
        updatedJournal.day.meals.find((meal) => meal.uuid == sourceMeal.uuid) == null;

    if (deleteSourceMeal) {
        void server.journal.nutrition.delete_meal({meal_uuid: sourceMeal.uuid});
    } else {
        void server.journal.nutrition.delete_entry({form: entry.uuid});
    }

    return updatedJournal;
}

export function updateEntryInJournal({
    journal,
    updateEntry,
    sourceMeal,
    meal,
}: {
    journal: server.journal.day.Journal.output;
    updateEntry: server.journal.nutrition.ExpandedEntry.output;
    sourceMeal: server.journal.nutrition.Meal.output;
    meal: server.journal.nutrition.StagedMeal.output;
}) {
    const updatedDay = produce(journal.day, (draft) => {
        const sourceMealDraft = draft.meals.find(
            (meal) => meal.uuid == sourceMeal.uuid,
        )!;
        const sourceIndex = sourceMealDraft.entries.findIndex(
            (e) => e.uuid == updateEntry.uuid,
        );
        const targetMeal = draft.meals.find((m) => mealMatches(m, meal));

        sourceMealDraft.entries.splice(sourceIndex, 1);

        if (!mealMatches(sourceMeal, meal)) {
            if (targetMeal != null) {
                targetMeal.entries.push(updateEntry);
            } else {
                addMealToDayDraft(draft, {
                    uuid: crypto.randomUUID(),
                    share_uuid: "",
                    key: journal.day.key,
                    recreate_recipe: null,
                    name: meal.name,
                    hour: meal.hour,
                    entries: [updateEntry],
                    type: meal.type,
                });
            }
        } else {
            sourceMealDraft.entries.splice(sourceIndex, 0, updateEntry);
        }
    });

    void server.journal.day.update_day(
        {user_id: updatedDay.user_id, key: updatedDay.key},
        {...updatedDay, deleted_meal_uuids: []},
    );

    return produce(journal, (draft) => {
        draft.day = updatedDay;
    });
}

export function addEntriesToJournal({
    journal,
    meal,
    entries,
    dispatch,
    updated,
    preserveUUIDs,
    journal_mode,
}: {
    journal: server.journal.day.Journal.output;
    meal: server.journal.nutrition.StagedMeal.output;
    entries: server.journal.nutrition.ExpandedEntry.output[];
    journal_mode: "VISION" | "VOICE" | "BARCODE" | "FACTS" | "SEARCH";
    dispatch: AppDispatch;
    updated?: (currentStreak: number) => unknown;
    preserveUUIDs?: boolean;
}) {
    const createdEntries = entries.map((entry) => ({
        ...entry,
        expanded_recipe: entry.expanded_recipe.map((ingredient) => ({
            ...ingredient,
            uuid: preserveUUIDs == true ? ingredient.uuid : crypto.randomUUID(),
        })),
        uuid: preserveUUIDs == true ? entry.uuid : crypto.randomUUID(),
    }));

    const updatedJournal = produce(journal, (draftState) => {
        const targetMeal = draftState.day.meals.find((m) => mealMatches(m, meal));

        if (targetMeal != null) {
            targetMeal.entries.push(...createdEntries);
        } else {
            addMealToDayDraft(draftState.day, {
                uuid: crypto.randomUUID(),
                share_uuid: "",
                key: journal.day.key,
                recreate_recipe: null,
                name: meal.name,
                hour: meal.hour,
                entries: createdEntries,
                type: meal.type,
            });
        }
    });
    const updatedMeal = updatedJournal.day.meals.find((m) => mealMatches(m, meal));

    void server.journal.day
        .update_day(
            {user_id: journal.day.user_id, key: journal.day.key},
            {...updatedJournal.day, deleted_meal_uuids: []},
        )
        .then((result) => {
            if (result.type != "success") {
                return;
            }

            if (result.data == "SUBSCRIPTION_REQUIRED") {
                dispatch({
                    type: "NEEDS_SUBSCRIPTION",
                });
                router.push("profile.subscription", {});
                dispatch(
                    notify(
                        "Looks like you need a subscription. Please subscribe to continue using the service.",
                        {type: "info", unique: true},
                    ),
                );
            } else {
                void updated?.(result.data.streak);
            }
        });

    dispatch({
        type: "CREATE_JOURNAL_ENTRIES_SUCCESS",
        entries: createdEntries.map((entry) => ({
            entry,
            backend: entry.backend,
            backend_id: entry.backend_id,
            source: `${entry.backend}-${entry.backend_id}`,
            name: entry.name,
            unit: entry.unit,
            quantity: entry.quantity,
            is_cupboard: false,
        })),
        journal_mode,
        meal_mode: meal.type,
        count: entries.length,
        lastUpdatedMeal:
            updatedMeal != null
                ? {name: updatedMeal.name, uuid: updatedMeal.uuid}
                : null,
    });

    return {updatedJournal, entryUUIDs: createdEntries.map((entry) => entry.uuid)};
}

export function addEntriesToCupboard({
    entries,
    dispatch,
}: {
    entries: server.journal.nutrition.ExpandedEntry.output[];
    dispatch: AppDispatch;
}) {
    const createdEntries = entries.map((entry) => ({
        ...entry,
        expanded_recipe: entry.expanded_recipe.map((ingredient) => ({
            ...ingredient,
            uuid: crypto.randomUUID(),
        })),
        uuid: crypto.randomUUID(),
    }));

    void server.journal.nutrition.update_cupboard({
        entries: createdEntries,
    });

    const pluralized = entries.length === 1 ? "Item" : "Items";
    dispatch(notify(`${pluralized} saved for later use.`));
    dispatch({
        type: "CREATE_JOURNAL_ENTRIES_SUCCESS",
        entries: createdEntries.map((entry) => ({
            entry,
            backend: entry.backend,
            backend_id: entry.backend_id,
            source: `${entry.backend}-${entry.backend_id}`,
            name: entry.name,
            unit: entry.unit,
            quantity: entry.quantity,
            is_cupboard: true,
        })),
        count: entries.length,
        lastUpdatedMeal: null,
    });
}

export function generateTimeOptions(): Array<{value: number; label: string}> {
    return Array.from({length: MAX_MEAL_HOUR + 1}, (_, hour) => ({
        value: hour,
        label: hourToName(hour),
    }));
}

export type ActivityRow = {
    type: "ACTIVITY";
    activity: server.journal.day.WorkoutAggregate.input;
    hour: string;
    slot: number;
    name: string;
};

type JournalRow =
    | {
          type: "MEAL";
          meal: server.journal.nutrition.Meal.output;
      }
    | ActivityRow;

export function updateEntryInDay(
    day: server.journal.nutrition.Day.output,
    entryUUID: string,
    updater: (
        entry: server.journal.nutrition.ExpandedEntry.output,
    ) => server.journal.nutrition.ExpandedEntry.output,
) {
    return produce(day, (draft) => {
        for (const meal of draft.meals) {
            const idx = meal.entries.findIndex((e) => e.uuid === entryUUID);
            if (idx !== -1) {
                meal.entries[idx] = updater(meal.entries[idx]);
                return;
            }
        }
    });
}

export function removeEntryFromDay(
    day: server.journal.nutrition.Day.output,
    entryUUID: string,
) {
    return produce(day, (draft) => {
        for (const meal of draft.meals) {
            meal.entries = meal.entries.filter((e) => e.uuid !== entryUUID);
        }
    });
}

export function prepareJournalRows(
    day: server.journal.nutrition.Day.output,
    activities: server.journal.day.WorkoutAggregate.input[],
): JournalRow[] {
    // Create meal rows
    const mealRows: JournalRow[] = day.meals
        .filter((meal) => meal.name !== "__SUPPLEMENTS__")
        .map((meal) => ({
            type: "MEAL" as const,
            meal,
        }));

    // Create activity rows
    const activityRows: JournalRow[] = activities.map((activity) => {
        // Parse UTC start time and convert to activity's timezone. Intl is the
        // only way to convert into an arbitrary IANA timezone without a tz
        // library, but construction goes through dates.parseISO and the h23
        // output is wrapped: Safari can emit "24" at midnight.
        const startDate = dates.parseISO(activity.start_time);
        const clock =
            parseInt(
                new Intl.DateTimeFormat("en-US", {
                    timeZone: activity.timezone,
                    hour: "numeric",
                    hourCycle: "h23",
                }).format(startDate),
            ) % 24;
        // en-CA formats as yyyy-mm-dd, matching day.key for the late-tail check.
        const localDate = new Intl.DateTimeFormat("en-CA", {
            timeZone: activity.timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).format(startDate);
        const slot = localDate > day.key ? clock + LATE_MEAL_HOUR : clock;
        const activityType: Record<string, string | null> =
            server.core.constants.WORKOUT_ACTIVITY_TYPES;
        const name =
            activityType[activity.workout_activity_type] ??
            server.core.constants.WORKOUT_ACTIVITY_TYPES.other;

        return {
            type: "ACTIVITY" as const,
            activity,
            name,
            hour: hourToName(clock),
            slot,
        };
    });

    // Combine and sort along the day's extended timeline
    const combined = [...mealRows, ...activityRows].sort((a, b) => {
        const timeA = a.type === "MEAL" ? mealSortValue(a.meal) : a.slot;
        const timeB = b.type === "MEAL" ? mealSortValue(b.meal) : b.slot;

        return timeA - timeB;
    });

    return combined;
}
